import cn from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import fullscreenExitSvg from "../assets/fullscreen-exit.svg";
import fullscreenSvg from "../assets/fullscreen.svg";
import helpSvg from "../assets/help.svg";
import settingsSvg from "../assets/settings.svg";
import statsSvg from "../assets/stats.svg";
import { NUM_BOARDS, NUM_GUESSES } from "../consts";
import { formatTimeElapsed, MersenneTwister } from "../funcs";
import HeaderScroll from "./HeaderScroll";
import Switcher from "./Switcher";
import {
  showAboutPopup,
  showSettingsPopup,
  showStatsPopup,
  startPracticeGame,
  startGame,
  useSelector,
} from "../store";

// Declare typescript definitions for safari fullscreen stuff
declare global {
  interface Document {
    webkitFullscreenElement: Element | null;
    webkitExitFullscreen: () => void;
  }
  interface HTMLElement {
    webkitRequestFullscreen: () => void;
  }
}
function isFullscreen() {
  const element =
    document.fullscreenElement || document.webkitFullscreenElement;
  return Boolean(element);
}
function enterFullscreen() {
  const element = document.documentElement;
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  }
}
function exitFullscreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
}

export default function Header({ type = 'sedecordle' }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const length: number = Number(searchParams.get('length') || 5);
  const mode: string = searchParams.get('mode') || '';
  const isPractice = window.location.pathname === `/${type}/practice` || mode === 'free';
  const id = useSelector((s) => s.game.id);
  const practice = useSelector((s) => s.game.practice);
  let title: React.ReactNode = '';
  if (type === 'sedecordle') {
    title = isPractice ? 'free sedecordle' : `Daily #${id}`;
  } else if (type === 'sedec-order') {
    title = isPractice ? (
      <>
        free <span style={{ color: "#33bb33" }}>sedec</span>
        <span style={{ color: "rgb(255, 204, 0)" }}>order</span>
      </>
    ) : (
      <>
        <span style={{ color: "#33bb33", fontWeight: '600' }}>Sedec</span>
        <span style={{ color: "rgb(255, 204, 0)", fontWeight: '600' }}>order</span> #{id}
      </>
    )
  } else if (type === 'savior') {
    title = isPractice ? 'free savior' : `Savior #${id}`;
  }

  // Refs so that the buttons are blurred on press
  // so that pressing enter again does not cause the
  // button to be activated again
  const practiceRef = useRef<HTMLButtonElement>(null);
  const newRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const handlePracticeClick = () => {
    practiceRef.current?.blur();
  };

  const handleNewClick = () => {
    newRef.current?.blur();
    const res = window.confirm(
      `Are you sure you want to start a new practice ${type[0].toUpperCase()}${type.substring(1)}?\n(Your current progress will be lost)`
    );
    if (!res) return;
    const id = MersenneTwister().u32();
    dispatch(startPracticeGame({ id, practice: true, length, type }));
    window.location.href = window.location.href
  };

  // ctrl/cmd + shift + r keyboard shortcut to reset practice mode quickly
  const [reset, setReset] = useState(false);
  useEffect(() => {
    const handler = (k: KeyboardEvent) => {
      const useCmdKey = navigator.platform.match(/mac|iphone|ipad/i);
      if (
        practice &&
        k.key === "r" &&
        !k.shiftKey &&
        !k.altKey &&
        ((useCmdKey && !k.ctrlKey && k.metaKey) ||
          (!useCmdKey && k.ctrlKey && !k.metaKey))
      ) {
        k.preventDefault();
        const id = MersenneTwister().u32();
        dispatch(startGame({ id, practice: true, length, type }));
        setReset(true);
        setTimeout(() => setReset(false), 500);
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [dispatch, practice, length, type]);

  // Fullscreen
  const [fullscreen, setFullscreen] = useState(isFullscreen);
  useEffect(() => {
    const handler = () => {
      setFullscreen(isFullscreen);
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, []);
  const handleFullscreenClick = () => {
    if (isFullscreen()) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  const switchPage = (link: any) => {
    navigate(link);
  };

  return (
    <div className="header">
      <div className="row-1">
        {isPractice ? (
          <>
            <button
              style={{width: '25%'}}
              className="mode-switch"
              ref={backRef}
              // onClick={() => PracticeBackPage(localStorage.getItem("prevUrl"))}
              onClick={() => window.location.href = '/'}
            >
              back
            </button>
            <button
              style={{width: '20%'}}
              className="mode-switch"
              ref={newRef}
              onClick={handleNewClick}
            >
              new
            </button>
          </>
        ) : (
          <>
            <button
            style={{width: '25%'}}
              className="mode-switch"
              ref={practiceRef}
              // onClick={() =>
              //   PracticePage(`${window.location.pathname}/practice`)
              // }
              onClick={() => window.location.href = '/'}
            >
              back
            </button>

          </>
        )}
        <p className="title">
          {title}
        </p>
        <button className="icon" onClick={() => switchPage("about")}> 
          <img className="rounded-full bg-black" src={helpSvg} alt="Help" />
        </button>
        <button className="icon" onClick={handleFullscreenClick}>
          <img
            src={fullscreen ? fullscreenExitSvg : fullscreenSvg}
            alt="Go Fullscreen"
          />
        </button>
      </div>
      <div className="row-2">
        <HeaderScroll />
      </div>
    </div>
  );
}
